import React, { useState } from "react";
import {
  Box,
  Checkbox,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from "@mui/material";
import { theme } from "../../../themes/theme";
import EmptyResult from "../../rules/EmptyResult";
import PlusMoreLabel from "../../../components/TableItems/PlusMoreLabel";
import { OrgUser } from "../OrgUser";
import UserDetailModal from "../UserDetailModal";
import { RoleState } from "../../../types/models/RoleState";
import { RoleBundle } from "../../../types/models/RoleBundle";
import { KnownRole } from "../../../types/models/KnownRole";
import { KnownLocation } from "../../../types/models/KnownLocation";
import { AppState } from "../../../redux/store";
import { useSelector } from "react-redux";
import {
  getLocationDisplayName,
  getRoleBundleDisplayName,
  getRoleDisplayName,
} from "../orgUserDisplay";

type SortableColumn =
  | "name"
  | "email"
  | "roleBundles"
  | "optInRoles"
  | "locations"
  | "organisation";

interface Column {
  key: SortableColumn;
  label: string;
}

const baseColumns: Column[] = [
  { key: "name", label: "Name" },
  { key: "email", label: "Email" },
  { key: "roleBundles", label: "Role Bundles" },
  { key: "optInRoles", label: "Opt-in Roles" },
  { key: "locations", label: "Locations" },
];

const organisationColumn: Column = { key: "organisation", label: "Organisation" };

const getSortValue = (
  user: OrgUser,
  column: SortableColumn,
  roleBundles: RoleBundle[] | undefined,
  knownRoles: KnownRole[] | undefined,
  knownLocations: KnownLocation[] | undefined
) => {
  switch (column) {
    case "name":
      return (user.Name || user.Email).toLowerCase();
    case "email":
      return user.Email.toLowerCase();
    case "roleBundles":
      return (user.RoleBundleIds ?? [])
        .map((id) => getRoleBundleDisplayName(id, roleBundles))
        .join(", ")
        .toLowerCase();
    case "optInRoles":
      return (user.OptInRoleIds ?? [])
        .map((id) => getRoleDisplayName(id, knownRoles))
        .join(", ")
        .toLowerCase();
    case "locations":
      return (user.LniIds ?? [])
        .map((id) => getLocationDisplayName(id, knownLocations))
        .join(", ")
        .toLowerCase();
    case "organisation":
      return (user.Organisation ?? "").toLowerCase();
    default:
      return "";
  }
};

const createPlusMore = (values: string[]) => {
  if (values.length === 0) return null;
  if (values.length === 1) return values[0];
  return (
    <Box sx={{ display: "flex", alignItems: "flex-end" }}>
      {values[0]}
      <PlusMoreLabel moreCount={values.length - 1} />
    </Box>
  );
};

const createRoleBundlesCell = (roleBundleIds: string[], roleBundles: RoleBundle[] | undefined) =>
  createPlusMore(roleBundleIds.map((id) => getRoleBundleDisplayName(id, roleBundles)));

const NoValue: React.FC<{ label: string }> = ({ label }) => (
  <Typography sx={{ fontSize: 13, color: theme.table.subTextColor }}>{label}</Typography>
);

interface UserAdministrationTableProps {
  users: OrgUser[];
  isAdmin: boolean;
  onUpdateUser: (user: OrgUser) => void;
  selectedUsers: string[];
  onSelectedUsersChange: (selectedUsers: string[]) => void;
}

const UserAdministrationTable: React.FC<UserAdministrationTableProps> = ({
  users,
  isAdmin,
  onUpdateUser,
  selectedUsers,
  onSelectedUsersChange,
}) => {
  const [orderBy, setOrderBy] = useState<SortableColumn>("name");
  const [orderDirection, setOrderDirection] = useState<"asc" | "desc">("asc");
  const [selectedUser, setSelectedUser] = useState<OrgUser>();
  const [openUserDetail, setOpenUserDetail] = useState<boolean>(false);

  const roleState = useSelector<AppState, RoleState>((state) => state.role);

  const columns = isAdmin ? [...baseColumns, organisationColumn] : baseColumns;

  const onClickUser = (user: OrgUser) => {
    setSelectedUser(user);
    setOpenUserDetail(true);
  };

  const handleSortRequest = (column: SortableColumn) => {
    const isAsc = orderBy === column && orderDirection === "asc";
    setOrderDirection(isAsc ? "desc" : "asc");
    setOrderBy(column);
  };

  const forward = orderDirection === "asc" ? 1 : -1;
  const sortedUsers = [...users].sort((a, b) => {
    const valueA = getSortValue(
      a,
      orderBy,
      roleState.roleBundles,
      roleState.knownRoles?.roles,
      roleState.knownRoles?.locations
    );
    const valueB = getSortValue(
      b,
      orderBy,
      roleState.roleBundles,
      roleState.knownRoles?.roles,
      roleState.knownRoles?.locations
    );
    if (valueA === valueB) return 0;
    return valueA > valueB ? forward : -forward;
  });

  const visibleSelectedCount = users.filter((user) => selectedUsers.includes(user.Id)).length;

  const handleSelectAll = () => {
    const visibleUserIds = users.map((user) => user.Id);
    if (visibleSelectedCount === 0) {
      // Add the currently visible rows to whatever's already selected, rather than replacing the whole selection
      const merged = new Set([...selectedUsers, ...visibleUserIds]);
      onSelectedUsersChange(Array.from(merged));
    } else {
      // Only deselect what's currently visible; leave hidden selections untouched.
      onSelectedUsersChange(selectedUsers.filter((id) => !visibleUserIds.includes(id)));
    }
  };

  const handleSelectUser = (userId: string) => {
    if (selectedUsers.includes(userId)) {
      onSelectedUsersChange(selectedUsers.filter((id) => id !== userId));
    } else {
      onSelectedUsersChange([...selectedUsers, userId]);
    }
  };

  return (
    <TableContainer component={Paper} sx={{ borderRadius: 3 }}>
      <Table size="small">
        <TableHead sx={{ backgroundColor: theme.table.subHeaderBgColor }}>
          <TableRow>
            <TableCell colSpan={2} sx={{ mt: 2, whiteSpace: "nowrap" }}>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Checkbox
                  checked={users.length > 0 && visibleSelectedCount === users.length}
                  indeterminate={visibleSelectedCount > 0 && visibleSelectedCount < users.length}
                  onChange={handleSelectAll}
                />
              </Box>
            </TableCell>
            {columns.map((column) => (
              <TableCell key={column.key} onClick={() => handleSortRequest(column.key)}>
                <TableSortLabel
                  active={orderBy === column.key}
                  direction={orderBy === column.key ? orderDirection : "asc"}
                >
                  {column.label}
                </TableSortLabel>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody
          sx={{
            "& .MuiTableRow-root:hover": {
              backgroundColor: theme.table.hoverBgColor,
            },
          }}
        >
          {sortedUsers.length === 0 ? (
            <TableRow>
              <TableCell sx={{ p: 0 }} colSpan={columns.length + 2}>
                <EmptyResult>No users available</EmptyResult>
              </TableCell>
            </TableRow>
          ) : (
            sortedUsers.map((user) => (
              <TableRow
                key={user.Id ?? user.Email}
                hover
                onClick={() => onClickUser(user)}
                sx={{ cursor: "pointer" }}
              >
                <TableCell colSpan={2} onClick={(event) => event.stopPropagation()}>
                  <Checkbox
                    checked={selectedUsers.includes(user.Id)}
                    onChange={() => handleSelectUser(user.Id)}
                  />
                </TableCell>
                <TableCell>{user.Name || user.Email}</TableCell>
                <TableCell>{user.Email}</TableCell>
                <TableCell>
                  {createRoleBundlesCell(user.RoleBundleIds ?? [], roleState.roleBundles) ?? (
                    <NoValue label="No role bundles assigned" />
                  )}
                </TableCell>
                <TableCell>
                  {createPlusMore(
                    (user.OptInRoleIds ?? []).map((id) =>
                      getRoleDisplayName(id, roleState.knownRoles?.roles)
                    )
                  ) ?? <NoValue label="No opt-in roles assigned" />}
                </TableCell>
                <TableCell>
                  {createPlusMore(
                    (user.LniIds ?? []).map((id) =>
                      getLocationDisplayName(id, roleState.knownRoles?.locations)
                    )
                  ) ?? <NoValue label="No locations assigned" />}
                </TableCell>
                {isAdmin && (
                  <TableCell>
                    {user.Organisation ?? <NoValue label="No organisation assigned" />}
                  </TableCell>
                )}
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
      <UserDetailModal
        open={openUserDetail}
        onClose={() => setOpenUserDetail(false)}
        user={selectedUser}
        isAdmin={isAdmin}
        onSave={onUpdateUser}
      />
    </TableContainer>
  );
};

export default UserAdministrationTable;
